import type { CSSProperties, ReactNode } from "react";
import {
  defaultPhotoFrame,
  defaultScrapbookPlacement,
  photoRatio,
  type GalleryTheme,
  type Photo,
  type PhotoFrame,
  type ScrapbookPlacement
} from "./model";

type FrameVariables = CSSProperties & {
  "--frame-color": string;
  "--frame-width": string;
  "--photo-ratio": string;
};

export function FramedPhoto({
  photo,
  frame = photo.frame,
  loading = "lazy",
  badges
}: {
  photo: Photo;
  frame?: PhotoFrame;
  loading?: "eager" | "lazy";
  badges?: ReactNode;
}) {
  const style: FrameVariables = {
    "--frame-color": frame.color,
    "--frame-width": `${frame.width}%`,
    "--photo-ratio": photoRatio(photo)
  };

  return (
    <span className={`framed-photo frame-${frame.style}`} style={style} data-photo-id={photo.id}>
      <span className="frame-decoration" aria-hidden="true" />
      <span className="framed-photo-image"><img src={photo.mediaUrl} alt="" loading={loading} /></span>
      {badges}
    </span>
  );
}

type ScrapbookVariables = CSSProperties & {
  "--desktop-x": string;
  "--desktop-y": string;
  "--desktop-width": string;
  "--desktop-rotation": string;
  "--desktop-z": string;
  "--mobile-x": string;
  "--mobile-y": string;
  "--mobile-width": string;
  "--mobile-rotation": string;
  "--mobile-z": string;
};

export function ScrapbookGallery({
  slug,
  photos,
  theme
}: {
  slug: string;
  photos: Photo[];
  theme: GalleryTheme;
}) {
  return (
    <section className={`scrapbook-public scrapbook-page-${theme.scrapbookPage}`} aria-label="Scrapbook gallery">
      {photos.map((photo, index) => {
        const placement = resolvePlacement(photo, index);
        return (
          <a
            className="scrapbook-item"
            href={`/g/${slug}/photo/${photo.id}`}
            key={photo.id}
            aria-label={`Open photo ${index + 1}`}
            style={scrapbookVariables(placement)}
          >
            <FramedPhoto photo={photo} frame={resolveFrame(photo, index)} />
          </a>
        );
      })}
    </section>
  );
}

export function resolveFrame(photo: Photo, index: number) {
  return photo.scrapbook ? photo.frame : defaultPhotoFrame(index);
}

export function resolvePlacement(photo: Photo, index: number) {
  return photo.scrapbook || defaultScrapbookPlacement(index);
}

function scrapbookVariables(placement: ScrapbookPlacement): ScrapbookVariables {
  return {
    "--desktop-x": `${placement.desktop.x}%`,
    "--desktop-y": `${placement.desktop.y}%`,
    "--desktop-width": `${placement.desktop.width}%`,
    "--desktop-rotation": `${placement.desktop.rotation}deg`,
    "--desktop-z": String(placement.desktop.z),
    "--mobile-x": `${placement.mobile.x}%`,
    "--mobile-y": `${placement.mobile.y}%`,
    "--mobile-width": `${placement.mobile.width}%`,
    "--mobile-rotation": `${placement.mobile.rotation}deg`,
    "--mobile-z": String(placement.mobile.z)
  };
}
